import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFilters } from "../../../customHooks/useFilters";
import {
  resetFilter,
  selectMasterSpareFilter,
  setFilterOption,
} from "../../../store/master/masterSpareSlice";
import { useGetMasterSpareFiltersQuery } from "../../../services/master/masterSpareFilterApiSlice";

import { SpareBrandModelFilterOptions } from "../../../component/mastersFilters/mastersSparesFilters/SpareBrandModelOptions";
import { SparePartOptions } from "../../../component/mastersFilters/mastersSparesFilters/SparePartOptions";

import mobile_filter from "../../../assets/mobile_filter.svg";
import classes from "./masterSparesFilter.module.css";


import { useNavigate } from "react-router-dom";

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { x: "-100%" },
  visible: {
    x: 0,
    transition: { type: "tween", duration: 0.35 },
  },
  exit: { x: "-100%", transition: { duration: 0.25 } },
};

export const MasterSparesFilterPage = () => {
  const {
    filters: reduxFilters,
    setFilters,
    clearAll,
  } = useFilters({
    selector: selectMasterSpareFilter,
    action: setFilterOption,
    resetAction: resetFilter,
    arrayFields: ["brand", "model", "spare"],
  });
  const filterRefs = useRef({});
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilterKey, setSelectedFilterKey] = useState(null);
  const [modalFilters, setModalFilters] = useState({});
  const navigate = useNavigate();

  const {
    data: filters,
    isLoading,
    isError,
  } = useGetMasterSpareFiltersQuery({
    category: "spares",
  });

  const quickTrayFilters = useMemo(
    () => filters?.filter((f) => f.is_quick_access) ?? [],
    [filters]
  );
  const isFilterActive = (filter) => {
    const f = reduxFilters;

    switch (filter.filter) {
      case "brand":
        return Boolean(f.brand?.length) || Boolean(f.model?.length);
      case "spare":
        return Boolean(f.spare?.length);
      default:
        return false;
    }
  };
  const renderOptions = (filter) => {
    if (!filter) return null;

    switch (filter.filter) {
      case "brand":
        return (
          <SpareBrandModelFilterOptions
            brandData={filter}
            brands={modalFilters.brand || []}
            models={modalFilters.model || []}
            onChange={(updated) =>
              setModalFilters((prev) => ({
                ...prev,
                ...updated,
              }))
            }
          />
        );

      case "spare":
        return (
          <SparePartOptions
            sparePartsData={filter}
            spares={modalFilters.spare}
            onChange={(updated) =>
              setModalFilters((prev) => ({
                ...prev,
                ...updated,
              }))
            }
          />
        );

      default:
        return null;
    }
  };

  const handleOpenModal = (filterKey = null) => {
    setSelectedFilterKey(filterKey ?? quickTrayFilters[0]?.filter ?? null);
    setModalFilters({ ...reduxFilters }); // sync Redux filters to modal
    setIsOpen(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...modalFilters });
    setIsOpen(false);
  };

  const handleClearFilter = (filterKey) => {
    switch (filterKey) {
      case "brand":
        setModalFilters((prev) => ({ ...prev, brand: [], model: [] }));
        setFilters((prev) => ({ ...prev, brand: [], model: [] })); // update Redux
        break;
      case "spare":
        setModalFilters((prev) => ({ ...prev, spare: [] }));
        setFilters((prev) => ({ ...prev, spare: [] })); // update Redux
        break;
      case "price":
        setModalFilters((prev) => ({ ...prev, start: null, end: null }));
        setFilters((prev) => ({ ...prev, start: null, end: null })); // update Redux
        break;

      default:
        break;
    }
  };
  
  const handleAdd = () => {
    navigate("/masters/spares/add")
  };

  const handleClearAll = () => {
    clearAll();
    setSelectedFilterKey(quickTrayFilters[0]?.filter ?? null);
    setIsOpen(false);
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);
  return (
    <section className={classes.box}>
      {/* Main Filters Button */}
      <section className={classes.box__filters}>
        <section className={classes.box__filters__main}>
          <button
            type="button"
            className={classes.box__filters__tab}
            onClick={() => handleOpenModal()}
          >
            <img
              alt="filter icon"
              src={mobile_filter}
              className={classes.box__filters__tab__icon}
            />
            <h1 className={classes.box__filters__tab__title}>Filters</h1>
          </button>
          <span className={classes.box__filters__main__sep} />
        </section>

        {/* Quick Filters */}
        <section
          className={classes.box__quick}
          role="tablist"
          aria-label="Quick filters"
        >
          <motion.div className={classes.box__filters__quick__tray}>
            {quickTrayFilters?.map((filter) => (
              <div
                key={filter.filter}
                ref={(el) => (filterRefs.current[filter.key] = el)}
                role="tab"
                tabIndex={0}
                className={`${classes.box__filters__quick__tray__btn} ${
                  isFilterActive(filter)
                    ? classes.box__filters__quick__tray__btn__applied
                    : ""
                }`}
                onClick={() => handleOpenModal(filter.filter)}
              >
                <h3
                  className={`${classes.box__filters__quick__tray__btn__text} ${
                    isFilterActive(filter)
                      ? classes.box__filters__quick__tray__btn__text__applied
                      : ""
                  }`}
                >
                  {filter.label}
                </h3>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFilter(filter.filter);
                  }}
                  className={`${
                    classes.box__filters__quick__tray__btn__close
                  } ${
                    isFilterActive(filter)
                      ? classes.box__filters__quick__tray__btn__close__active
                      : ""
                  }`}
                >
                  ×
                </span>
                {!isFilterActive(filter) && (
                  <span style={{ color: "#2f3567", paddingBottom: "4px" }}>
                    ⏷
                  </span>
                )}
              </div>
            ))}
          </motion.div>
          
        </section>
        <button className={classes.box__btn__add} onClick={handleAdd}>
            Add Spares
          </button>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.section
            className={classes.box__filters__overlay}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
             onClick={() => setIsOpen(false)} 
          >
            <motion.section
              className={classes.box__filters__modal}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={classes.box__filters__modal__head}>
                <div className={classes.box__filters__modal__head__content}>
                  <img
                    alt="filter icon"
                    src={mobile_filter}
                    className={classes.box__filters__tab__icon}
                  />
                  <h4 className={classes.box__filters__modal__head__title}>
                    Filters
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className={classes.box__filters__modal__close}
                >
                  X
                </button>
              </div>
              <div className={classes.box__filters__modal__content}>
                <div className={classes.box__space__wrapper}>
                  <div className={classes.box__filters__modal__content__left}>
                    {quickTrayFilters.map((filter) => (
                      <button
                        type="button"
                        key={filter.filter}
                        ref={(el) => (filterRefs.current[filter.key] = el)}
                        role="tab"
                        tabIndex={0}
                        className={`${
                          classes.box__filters__modal__content__left__btn
                        } ${
                          selectedFilterKey === filter.filter
                            ? classes.box__filters__modal__content__left__btn__active
                            : isFilterActive(filter)
                            ? classes.box__filters__modal__content__left__btn__applied
                            : ""
                        }`}
                        onClick={() => setSelectedFilterKey(filter.filter)}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                  <div className={classes.box__filters__modal__content__sep} />
                </div>

                <div className={classes.box__filters__modal__content__right}>
                  {selectedFilterKey ? (
                    renderOptions(
                      filters?.find((f) => f.filter === selectedFilterKey)
                    )
                  ) : (
                    <p className={classes.box__filters__modal__placeholder}>
                      Select a filter to view options
                    </p>
                  )}
                </div>
              </div>
              <div className={classes.box__filters__modal__footer}>
                <button
                  className={`${classes.box__filters__modal__footer__btn} ${classes.box__filters__modal__footer__btn__cancel}`}
                  onClick={handleClearAll}
                >
                  Clear All
                </button>
                <button
                  className={`${classes.box__filters__modal__footer__btn} ${classes.box__filters__modal__footer__btn__show}`}
                  onClick={handleApplyFilters}
                >
                  Apply
                </button>
              </div>
            </motion.section>
          </motion.section>
        )}
      </AnimatePresence>
    </section>
  );
};
